import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaMoneyCheckAlt, FaAward, FaGlobe, FaWhatsapp, FaPlay } from 'react-icons/fa';
import PageHero from '../../components/PageHero/PageHero';
import { certifications, galleryItems, WA_LINK } from '../../data/data';
import paris    from '../../assets/paris.jpg';
import masjidNew from '../../assets/masjibNew.jpg';
import arabiaBg  from '../../assets/arabia.jpg';
import './About.css';


const WHY_US = [
  {
    icon:  FaMoneyCheckAlt,
    title: 'Competitive Pricing',
    desc:  'We negotiate the best deals so you get maximum value for every naira spent on your journey.',
  },
  {
    icon:  FaAward,
    title: 'Award-Winning Service',
    desc:  'IATA-accredited agents with over a decade of experience serving thousands of satisfied clients.',
  },
  {
    icon:  FaGlobe,
    title: 'Worldwide Coverage',
    desc:  'From Makkah to Paris, we handle every destination with the same professionalism and care.',
  },
];

export default function About() {
  const [playingVideo, setPlayingVideo] = useState(null);

  return (
    <>
      <PageHero
        title="About"
        highlight="Us"
        subtitle="Over a decade of trusted travel service across Nigeria — IATA accredited, NAHCON licensed, and client-first."
        bgImage={arabiaBg}
      />

      {/* ── Company Story ── */}
      <section className="story-section section-pad">
        <div className="container">
          <div className="story-inner">
            <motion.div
              className="story-images"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <img src={masjidNew} alt="Haram Mosque" className="story-img story-img--main" />
              <img src={paris}     alt="Paris"        className="story-img story-img--accent" />
            </motion.div>

            <motion.div
              className="story-text"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="section-label">Our Story</p>
              <h2 className="section-title">We Provide the Best Tour Packages <span>In Your Budget</span></h2>
              <p className="story-body">
                AL-AMANAT Travels is a company that has been in existence for more than a decade with many 
                successful achievements in their operations. Alhaji Abdullahi Olaseni Kamaldeen is the 
                Managing Director and CEO of the company.
              </p>
              <p className="story-body">
                He manages the company with his Western and Islamic knowledge, along with his expertise 
                gained from various aviation courses. He is supported by a dedicated team of staff members 
                who are also certified in IATA and other professional courses — making AL-AMANAT stand out 
                among travel agencies in Nigeria.
              </p>
              <p className="story-body">
                AL-AMANAT Travels is an accredited IATA agent company and a member of leading travel 
                organisations worldwide. When doing business with us, you will never regret it — 
                because we lead, others follow.
              </p>
              <a href={WA_LINK} target="_blank" rel="noreferrer" className="btn-primary">
                <FaWhatsapp /> Get In Touch
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="why-section section-pad">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Why AL-AMANAT</p>
            <h2 className="section-title">Why Choose <span>Us</span></h2>
          </div>
          <div className="why-grid">
            {WHY_US.map((item, i) => (
              <motion.div
                key={i}
                className="why-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.12 }}
              >
                <div className="why-card__icon">
                  <item.icon size={30} />
                </div>
                <h3 className="why-card__title">{item.title}</h3>
                <p  className="why-card__desc">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="cert-section section-pad">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Accreditations</p>
            <h2 className="section-title">Licensed & <span>Certified</span></h2>
            <p className="section-desc" style={{ maxWidth: 480 }}>
              We are fully accredited by leading national and international aviation and travel bodies.
            </p>
          </div>
          <div className="cert-grid">
            {certifications.map((cert, i) => (
              <motion.div
                key={i}
                className="cert-item"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
              >
                <img src={cert.url} alt={cert.name} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gallery ── */}
      <section className="gallery-section section-pad">
        <div className="container">
          <div className="section-header" id="gallery">
            <p className="section-label">Media</p>
            <h2 className="section-title">Our <span>Gallery</span></h2>
            <p className="section-desc">A glimpse of the journeys, moments, and memories we share with our travellers.</p>
          </div>
          <div className="gallery-grid">
            {galleryItems.map((item, i) => (
              <motion.div
                key={i}
                className={`gallery-item ${item.type === 'video' ? 'gallery-item--video' : ''} ${playingVideo === i ? 'gallery-item--playing' : ''}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
              >
                {item.type === 'image' ? (
                  <img src={item.src} alt={item.alt || 'Gallery'} />
                ) : (
                  <video
                    src={item.src}
                    controls
                    preload="metadata"
                    playsInline
                    onPlay={() => setPlayingVideo(i)}
                    onPause={() => setPlayingVideo(null)}
                    onEnded={() => setPlayingVideo(null)}
                  />
                )}
                <div className="gallery-item__overlay">
                  {item.type === 'video' && <FaPlay aria-hidden="true" />}
                  <span>{item.type === 'video' ? 'Watch journey moment' : 'AL-AMANAT in motion'}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
